import { RestRequest, RestContext, ResponseFunction } from 'msw'
import { cloneDeep, get } from 'lodash'
import {
  storeEntityDataListToLocalForage,
  _storeEntityDataListToLocalForage,
} from './mockDataStorageManager'
import { getNextVersionNumberString } from './getNextVersionNumberString'
import { parsePostedEntity } from './parsePostedEntity'
import {
  doEnforceMockVersionConflicts,
  findVersionConflict,
  isStaleVersion,
  versionConflictBody,
} from './versionConflict'
import { deleteConflictBody, findDeleteConflict } from './deleteConflict'
import { ensureUniqueness } from '~/cynqueModules/ensureUniqueness'
import { assert, assertIsNotUndefined } from '~/types/models'
import type {
  TEntityId,
  THrid,
  TEntityData,
  IEntityDataItem,
  IEntityDataItemForPost,
  TEntityDataForPost,
  TEntityName,
  TEntityVersion,
} from '~/types/models'

const mn = '[api/msw/api/modules/mockDataCommon.ts]'

// NOTE: Tight typing currently not working because some entities
// return other things, such as TFieldSetFieldItem[]. Need to address.  JR
interface IGetEntityDataListFn {
  (): IEntityDataItem[]
}

export async function getEntityDataById(
  id: TEntityId,
  getEntityListDataFn: Function // IGetEntityDataListFn see note above. JR
): Promise<IEntityDataItem> {
  const fn = `${mn} getEntityDataById |`
  const { targetItem } = await _getTargetItem(fn, id, getEntityListDataFn)
  return targetItem
}

interface PostResponse {
  data: TEntityData
  // This isn't exactly right, cause version is optional in the TEntityData type
  // and specifically shouldn't be here, but it doesn't work to create a version of TEntityData
  // using a new interface which defines an IEntityDataItemWithVersionString and a
  // TEntityDataWIthVersionString type consisting of the simple and array versions of that
  // interface even though TEntityData uses that same construction as does work.  JR.
}

export async function addToEntityListData(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext,
  getEntityListDataFn: Function, // IGetEntityDataListFn
  postProcessFn?: Function,
  postProcessOnceForAllFn?: Function
) {
  const newEntityData: TEntityDataForPost = await parsePostedEntity(req)
  const data: TEntityData = await addNewEntityDataToEntityListData({
    req,
    newEntityData,
    getEntityListDataFn,
    postProcessFn,
    postProcessOnceForAllFn,
  })
  const responseBody: PostResponse = { data }
  return res(ctx.status(201), ctx.json(responseBody))
}

export async function addNewEntityDataToEntityListData({
  req,
  entityKeyBaseOverride = undefined,
  newEntityData,
  getEntityListDataFn,
  postProcessFn,
  postProcessOnceForAllFn,
}: {
  req: RestRequest
  entityKeyBaseOverride?: TEntityName | undefined
  newEntityData: TEntityDataForPost
  getEntityListDataFn: Function
  postProcessFn?: Function
  postProcessOnceForAllFn?: Function
}): Promise<TEntityData> {
  const fn = `${mn} addNewEntityDataToEntityListData |`
  if (Array.isArray(newEntityData)) {
    console.debug(`${fn} Adding entities from array to entityListData`)
    const newEntityDataWithHridsAndVersions: IEntityDataItem[] = []
    for (const ned of newEntityData) {
      const newEntityDataWithHridAndVersion = await _addEntityDataToListData({
        entityData: ned,
        entityKeyBaseOverride,
        getEntityListDataFn,
        req,
        postProcessFn,
      })
      newEntityDataWithHridsAndVersions.push(newEntityDataWithHridAndVersion)
    }
    if (postProcessOnceForAllFn) {
      console.debug(`${fn} There -IS- a postProcessOnceForAllFn. Running it.`)
      await postProcessOnceForAllFn({ newEntityData })
      // Note: the postProcessOnceForAllFn must handle its own
      // call to storeEntityDataListToLocalForage. It's intended for
      // when you need to update entities other than the 'main' entity
      // in question, such as when propagating changes from Services up the
      // entity hierarchy.
    }
    return newEntityDataWithHridsAndVersions
  } else {
    console.debug(`${fn} Adding entity to entityListData`)
    const newEntityDataWithHridAndVersion = await _addEntityDataToListData({
      entityData: newEntityData,
      entityKeyBaseOverride,
      getEntityListDataFn,
      req,
      postProcessFn,
    })
    if (postProcessOnceForAllFn) {
      console.debug(`${fn} There -IS- a postProcessOnceForAllFn. Running it.`)
      await postProcessOnceForAllFn({ newEntityData })
      // Note: the postProcessOnceForAllFn must handle its own
      // call to storeEntityDataListToLocalForage. It's intended for
      // when you need to update entities other than the 'main' entity
      // in question, such as when propagating changes from Services up the
      // entity hierarchy.
    }
    return newEntityDataWithHridAndVersion
  }
}

export async function updateEntityListData(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext,
  getEntityListDataFn: Function, // IGetEntityDataListFn
  postProcessFns: Function[] = []
) {
  const fn = `${mn} updateEntityListData |`
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
      id,
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  try {
    const updatedEntityData: Partial<IEntityDataItem> = await req.json()
    let { targetItem, listData } = await _getTargetItem(
      fn,
      id,
      getEntityListDataFn
    )
    //
    const { version, ...restOfUpdatedEntityData } = updatedEntityData
    // Compare the submitted version to the stored one and reject if they differ,
    // the way the real server does. This used to be a comment saying it SHOULD
    // happen, next to code that did the opposite — see versionConflict.ts for
    // what that cost us.
    if (
      doEnforceMockVersionConflicts() &&
      isStaleVersion(version, targetItem.version)
    ) {
      console.debug(
        `${fn} 409 — submitted version '${version}' !==` +
          ` stored version '${targetItem.version}' for '${id}'`
      )
      return res(
        ctx.status(409),
        ctx.json(
          versionConflictBody({
            id,
            submittedVersion: version,
            storedVersion: targetItem.version,
          })
        )
      )
    }
    //
    const updatedEntityDataWithNewVersion = {
      version: getNextVersionNumberString(version),
      ...restOfUpdatedEntityData,
    }
    //
    Object.assign(targetItem, updatedEntityDataWithNewVersion)
    console.debug(`${fn} after updating`, await getEntityListDataFn())
    console.debug(`${fn} postProcessFns:`, postProcessFns)
    assert(
      !postProcessFns || Array.isArray(postProcessFns),
      `${fn} postProcessFns is not an array`
    )
    if (postProcessFns.length > 0) {
      console.debug(
        postProcessFns.length > 1
          ? `There -ARE- ${postProcessFns.length} postProcessFns. Running them.`
          : `There -IS- a postProcessFn. Running it.`
      )
      for (let i = 0; i < postProcessFns.length; i++) {
        await postProcessFns[i]({
          targetItem,
          updatedEntityData: updatedEntityDataWithNewVersion,
          listData,
        })
      }
      listData = await getEntityListDataFn()
      console.debug(`${fn} after post-processing`, listData)
    }
    await storeEntityDataListToLocalForage({ req, listData })
    const data = updatedEntityDataWithNewVersion
    const responseBody = { data }
    return res(ctx.status(200), ctx.json(responseBody))
  } catch (e) {
    console.error(e)
    const responseBody = {
      statusCode: 500,
      message: `${fn} updateEntityListData failed`,
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
}

export async function updateEntityListDataMultiple(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext,
  getEntityListDataFn: Function, // IGetEntityDataListFn
  postProcessFn?: Function,
  postProcessOnceForAllFn?: Function
) {
  const fn = `${mn} updateEntityListDataMultiple |`

  try {
    const updatedEntityDataMultiple: Partial<IEntityDataItem>[] =
      await req.json()

    // Enforced HERE, in the request handler, rather than inside
    // updateEntityListDataMultipleFromUpdatedEntityData — five internal cascades
    // call that helper directly (building an order from an opportunity, and the
    // three *MSServices seqNum flows) with data the mock itself constructed, not
    // data a client submitted. There is no client version to honour there, and
    // rejecting one would break a server-side flow rather than model one.
    const conflict = findVersionConflict({
      listData: await _getListData(fn, getEntityListDataFn),
      submittedRows: updatedEntityDataMultiple,
    })
    if (conflict) {
      console.debug(
        `${fn} 409 — submitted version '${conflict.submittedVersion}' !==` +
          ` stored version '${conflict.storedVersion}' for '${conflict.id}'`
      )
      return res(ctx.status(409), ctx.json(versionConflictBody(conflict)))
    }

    const updatedEntityDataWithNewVersions =
      await updateEntityListDataMultipleFromUpdatedEntityData({
        req,
        updatedEntityDataMultiple,
        getEntityListDataFn,
        postProcessFn,
        postProcessOnceForAllFn,
      })
    const data = updatedEntityDataWithNewVersions
    const responseBody = { data }
    return res(ctx.status(200), ctx.json(responseBody))
  } catch (e) {
    console.debug(`${fn} error...`, e)
    const responseBody = {
      statusCode: 500,
      message: `${fn} updateEntityListDataMultiple failed`,
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
}

export async function updateEntityListDataMultipleFromUpdatedEntityData({
  req,
  entityKeyBaseOverride = undefined,
  updatedEntityDataMultiple,
  getEntityListDataFn,
  postProcessFn,
  postProcessOnceForAllFn,
}: {
  req: RestRequest
  entityKeyBaseOverride?: TEntityName | undefined
  updatedEntityDataMultiple: Partial<IEntityDataItem>[]
  getEntityListDataFn: Function // IGetEntityDataListFn
  postProcessFn?: Function
  postProcessOnceForAllFn?: Function
}) {
  const fn = `${mn} updateEntityListDataMultipleFromUpdatedEntityData |`
  console.debug(`${fn} running`)
  console.debug(`${fn} updatedEntityDataMultiple:`, updatedEntityDataMultiple)

  try {
    const updatedEntityDataWithNewVersions: Partial<IEntityDataItem>[] = []
    let listData = await _getListData(fn, getEntityListDataFn)
    for (const updatedEntityData of updatedEntityDataMultiple) {
      const targetItem: IEntityDataItem | undefined = listData.find(
        (item) => item.id === updatedEntityData.id
      )
      if (!targetItem) {
        throw new Error(
          `${fn} targetItem is undefined. This should never happen.`
        )
      }
      //
      console.debug(
        `Before update...${fn} id: ${updatedEntityData.id} | version: ${updatedEntityData.version}`
      )
      // const { id: _, version, ...restOfUpdatedEntityData } = updatedEntityData // returned multi data should include ids
      const { version, ...restOfUpdatedEntityData } = updatedEntityData
      // On real server, should compare submitted version to currently stored
      // version and reject update if different
      //
      const updatedEntityDataWithNewVersion = {
        version: getNextVersionNumberString(version),
        ...restOfUpdatedEntityData,
      }
      console.debug(
        `After update...${fn} id: ${updatedEntityDataWithNewVersion.id} |` +
          ` version: ${updatedEntityDataWithNewVersion.version}`
      )
      //
      const { id: _, ...restOfUpdatedEntityDataLessId } =
        updatedEntityDataWithNewVersion
      Object.assign(targetItem, restOfUpdatedEntityDataLessId)

      if (postProcessFn) {
        console.debug(`${fn} There -IS- a postProcessFn. Running it.`)
        await postProcessFn({
          targetItem,
          updatedEntityDataWithNewVersion,
          listData,
        })
      }
      updatedEntityDataWithNewVersions.push(updatedEntityDataWithNewVersion)
    }
    if (postProcessOnceForAllFn) {
      console.debug(`${fn} There -IS- a postProcessOnceForAllFn. Running it.`)
      await postProcessOnceForAllFn({ updatedEntityDataMultiple, listData })
      // Note: the line above is using the version of the update data
      // with the 'old' version number. This could be an issue, although
      // with current onceForAll functions, it isn't.  JR
    }
    listData = await getEntityListDataFn()
    console.debug(`${fn} after post-processing`, listData)
    if (entityKeyBaseOverride) {
      await storeEntityDataListToLocalForage({
        req,
        entityKeyBaseOverride,
        listData,
      })
    } else {
      await storeEntityDataListToLocalForage({ req, listData })
    }
    console.debug(
      `${fn} successful. Returning updatedEntityDataWithNewVersions:`,
      updatedEntityDataWithNewVersions
    )
    return updatedEntityDataWithNewVersions
  } catch (e) {
    console.error(`${fn}`, e)
    throw e
  }
}

/**
 * The entity name to interpolate into a delete-conflict detail.
 *
 * Taken from the request path's collection segment (`/api/opportunitymsservices/x`
 * -> `opportunitymsservices`) because that is all the mock has. The real API
 * interpolates its entity CLASS name (`opportunityMSService`), so the casing
 * differs; the sentence, the field it arrives in and everything the client does
 * with it do not. Deliberately not faked into camelCase — inventing a name the
 * mock cannot actually know would make the two look identical while one of them
 * was guessing.
 */
function _entityNameFromRequest(req: RestRequest): string {
  // `req.url` is a URL here, not a string — `deleteEntityListDataMultiple`
  // already reads `req.url.searchParams` off it.
  const segments = req.url.pathname.split('/')
  const apiIndex = segments.indexOf('api')
  return (apiIndex >= 0 ? segments[apiIndex + 1] : segments[1]) || 'entity'
}

export async function deleteEntityListData(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext,
  getEntityListDataFn: Function, // IGetEntityDataListFn
  postProcessFn?: Function,
  postProcessOnceForAllFn?: Function
) {
  const fn = `${mn} deleteEntityListData |`
  console.debug(req)
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.debug(
    `${fn} Deleting entity with id '${id}' from from entityListData array`
  )
  // ⚠ REFUSE BEFORE REMOVING ANYTHING. See deleteConflict.ts.
  const deleteConflict = findDeleteConflict({
    listData: await getEntityListDataFn(),
    ids: [id],
  })
  if (deleteConflict) {
    console.debug(`${fn} refusing: '${id}' has canBeDeleted false`)
    return res(
      ctx.status(409),
      ctx.json(
        deleteConflictBody({
          entityName: _entityNameFromRequest(req),
          id: deleteConflict.id,
        })
      )
    )
  }
  const cloneOfLastDeletedItem = await _deleteOneItem(
    fn,
    req,
    id,
    getEntityListDataFn,
    postProcessFn
  )
  assertIsNotUndefined(cloneOfLastDeletedItem) // Should never be
  console.debug(`${fn} cloneOfLastDeletedItem`, cloneOfLastDeletedItem)
  if (postProcessOnceForAllFn) {
    console.debug(`${fn} There -IS- a postProcessOnceForAllFn. Running it.`)
    await postProcessOnceForAllFn({ deletedEntityData: cloneOfLastDeletedItem })
  }
  console.debug(getEntityListDataFn())
  return res(ctx.status(204))
}

export async function deleteEntityListDataMultiple(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext,
  getEntityListDataFn: Function, // IGetEntityDataListFn
  postProcessFn?: Function,
  postProcessOnceForAllFn?: Function
) {
  const fn = `${mn} deleteEntityListData |`
  const ids = req.url.searchParams.getAll('id')
  console.log('====================>', ids)
  if (!ids.every((id) => typeof id === 'string')) {
    const responseBody = {
      statusCode: 500,
      message: `Passed Ids array contains some elements that are not of type 'string'.`,
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.debug(
    `${fn} Deleting entities with ids '${ids} from entityListData array`
  )
  // ⚠ ONE REFUSAL FAILS THE WHOLE REQUEST, and it is checked BEFORE the loop
  // below removes anything. Deleting the deletable members and then refusing
  // would leave a kit half gone — the partial state a single DELETE of a whole
  // subtree exists to avoid, and one the real API cannot produce because the
  // FK violation aborts its transaction.
  const deleteConflict = findDeleteConflict({
    listData: await getEntityListDataFn(),
    ids,
  })
  if (deleteConflict) {
    console.debug(
      `${fn} refusing: '${deleteConflict.id}'` +
        ` (index ${deleteConflict.index}) has canBeDeleted false`
    )
    return res(
      ctx.status(409),
      ctx.json(
        deleteConflictBody({
          entityName: _entityNameFromRequest(req),
          id: deleteConflict.id,
        })
      )
    )
  }
  let cloneOfLastDeletedItem: IEntityDataItem | undefined
  for await (const id of ids) {
    cloneOfLastDeletedItem = await _deleteOneItem(
      fn,
      req,
      id,
      getEntityListDataFn,
      postProcessFn
    )
  }
  assertIsNotUndefined(cloneOfLastDeletedItem) // Should never be
  console.debug(`${fn} cloneOfLastDeletedItem`, cloneOfLastDeletedItem)
  if (postProcessOnceForAllFn) {
    console.debug(`${fn} There -IS- a postProcessOnceForAllFn. Running it.`)
    await postProcessOnceForAllFn({ deletedEntityData: cloneOfLastDeletedItem })
  }
  return res(ctx.status(204))
}

export async function ensureUniquenessAfterAddOrChangeToListData<
  T extends IEntityDataItem
>({
  targetItem,
  updatedEntityData,
  listData,
  propertyNameArray,
  entityKeyBase,
  getListDataFn,
  filterFn,
}: {
  targetItem: IEntityDataItem
  updatedEntityData: Partial<T>
  listData: T[]
  propertyNameArray: string[]
  entityKeyBase: string
  getListDataFn: Function
  filterFn: (obj: T) => {}
}) {
  const fn = `${mn} ensureUniquenessAfterAddOrChangeToListData |`
  console.debug(`${fn} ensuring uniqueness...`)

  const filteredEntityArray = listData.filter(filterFn)
  for (const propertyName of propertyNameArray) {
    if (!updatedEntityData.hasOwnProperty(propertyName)) {
      console.debug(
        `${fn} Change does not affect ${propertyName}.` +
          ` Not calling ensureUniqueness.`
      )
      continue
    }
    console.debug(
      `${fn} Change affects ${propertyName}.` + ` Calling ensureUniqueness.`
    )
    await ensureUniqueness({
      callingMn: fn,
      sourceEntityId: targetItem.id,
      sourceEntityFlagPropertyName: propertyName,
      sourceEntityArray: filteredEntityArray,
      sourceEntityData: updatedEntityData,
    })
    await _storeEntityDataListToLocalForage({
      entityKeyBase,
      listData: await getListDataFn(),
      callingFn: fn,
    })
  }
}

export async function propagateAfterAddsChangesOrDeletes<T>({
  sourceItem,
  sourceEntityName,
  targetEntityInSourceItem,
  getOverviewFn,
  propagationFn,
  getTargetEntityListDataFn,
  entityKeyBase,
}: {
  sourceItem: IEntityDataItem | Partial<IEntityDataItem>
  sourceEntityName: string
  targetEntityInSourceItem: string
  getOverviewFn: Function
  propagationFn: Function
  getTargetEntityListDataFn: Function
  entityKeyBase: string
}) {
  const fn = `${mn} propagateAfterAddsChangesOrDeletes |`
  console.debug(`${fn} propagating ${sourceEntityName}Data...`)
  console.debug(`${fn} ${sourceEntityName}`, sourceItem)
  const targetId = get(sourceItem, `${targetEntityInSourceItem}.id`)
  const overviewData = await getOverviewFn(targetId)
  console.debug(`${fn} overviewData`, overviewData)
  assertIsNotUndefined(overviewData) // should never be
  propagationFn({ overviewData })
  await _storeEntityDataListToLocalForage({
    entityKeyBase,
    listData: await getTargetEntityListDataFn(),
    callingFn: fn,
  })
}

//
// helper functions
//

async function _getListData(
  fn: string,
  getEntityListDataFn: Function // IGetEntityDataListFn
): Promise<IEntityDataItem[]> {
  const listData = await getEntityListDataFn()
  if (!listData) {
    throw new Error(`${fn} listData is falsey. This should never happen.`)
  }
  return listData
}

interface I_getTargetItem {
  targetItem: IEntityDataItem
  listData: IEntityDataItem[]
}

async function _getTargetItem(
  fn: string,
  id: TEntityId,
  getEntityListDataFn: Function // IGetEntityDataListFn
): Promise<I_getTargetItem> {
  const listData = await _getListData(fn, getEntityListDataFn)
  const targetItem: IEntityDataItem | undefined = listData.find(
    (item) => item.id === id
  )
  if (!targetItem) {
    throw new Error(`${fn} targetItem is undefined. This should never happen.`)
  }
  return { targetItem, listData }
}

export async function _addEntityDataToListData({
  entityData,
  getEntityListDataFn,
  req,
  entityKeyBaseOverride,
  postProcessFn,
}: {
  entityData: IEntityDataItemForPost
  getEntityListDataFn: Function // IGetEntityDataListFn,
  req: RestRequest
  entityKeyBaseOverride?: TEntityName
  postProcessFn?: Function
}): Promise<IEntityDataItem> {
  const fn = '_addEntityDataToListData |'
  const hrid = entityData.id.slice(2)
  entityData.hrid = hrid
  const version = '1' // Implementing debug version of version # -- increments by entity instance. Doesn't match production method.
  entityData.version = version
  let listData = await _getListData(fn, getEntityListDataFn)
  listData.push(entityData as IEntityDataItem) // We just added the 'hrid' above
  console.debug(`${fn} list after add:`, await getEntityListDataFn())
  if (postProcessFn) {
    console.debug(`${fn} There IS a postProcessFn. Running it.`)
    await postProcessFn({
      targetItem: entityData,
      updatedEntityData: entityData,
      listData,
    })
    listData = await getEntityListDataFn()
    console.debug(`${fn}, after post-processing`, listData)
  }
  if (entityKeyBaseOverride) {
    await storeEntityDataListToLocalForage({
      req,
      entityKeyBaseOverride,
      listData,
    })
  } else {
    await storeEntityDataListToLocalForage({ req, listData })
  }
  return entityData as IEntityDataItem // we added hrid and version above
}

async function _deleteOneItem(
  fn: string,
  req: RestRequest,
  id: TEntityId,
  getEntityListDataFn: Function, // IGetEntityDataListFn
  postProcessFn?: Function
): Promise<IEntityDataItem> {
  const { targetItem, listData } = await _getTargetItem(
    fn,
    id,
    getEntityListDataFn
  )
  // _getTargetItem throws an error if the target item doesn't exist.
  // If it does, it returns { targetItem, listData }
  const indexOfItemToDelete = listData.map((item) => item.id).indexOf(id)
  const deletedEntityData = cloneDeep(targetItem)
  console.debug(`${fn} index of item to delete: ${indexOfItemToDelete}`)
  listData.splice(indexOfItemToDelete, 1)
  if (postProcessFn) {
    console.debug(`${fn} There IS a postProcessFn. Running it.`)
    await postProcessFn({ deletedEntityData })
  }
  await storeEntityDataListToLocalForage({ req, listData })
  return deletedEntityData
}
